<app-modal [isOpen]="isOpen" (close)="cerrar()" className="max-w-3xl mx-4 sm:mx-6">
  <div class="p-6 space-y-5">
    <!-- Header -->
    <div>
      <h2 class="text-xl font-semibold text-gray-900 dark:text-white">Importar / Exportar CSV</h2>
      <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
        Procesamiento síncrono. Máximo 5 MB y 5000 filas por archivo. Requiere permiso de importar/exportar.
      </p>
    </div>

    <!-- Tabs -->
    <div class="flex gap-1 border-b border-gray-200 dark:border-gray-700">
      @for (tab of tabsList; track tab.key) {
        <button
          type="button"
          (click)="selectTab(tab.key)"
          class="px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors"
          [ngClass]="activeTab === tab.key
            ? 'border-brand-500 text-brand-600 dark:text-brand-400'
            : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'">
          {{ tab.label }}
        </button>
      }
    </div>

    <p class="text-sm text-gray-600 dark:text-gray-400">
      {{ currentTab.description }}
    </p>

    <!-- Acciones de export / template -->
    <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
      @if (canExport) {
        <button
          type="button"
          (click)="exportar()"
          [disabled]="exporting || importing"
          class="flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5m0 0l5-5m-5 5V4"></path>
          </svg>
          {{ exporting ? 'Exportando…' : 'Exportar CSV' }}
        </button>
      }

      @if (canImport) {
        <button
          type="button"
          (click)="descargarPlantilla()"
          [disabled]="downloadingTemplate || importing"
          class="flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
          </svg>
          {{ downloadingTemplate ? 'Descargando…' : 'Descargar plantilla' }}
        </button>
      }
    </div>

    <!-- Sección de importación -->
    @if (canImport) {
    <div class="space-y-3 pt-2 border-t border-gray-200 dark:border-gray-700">
      <h3 class="text-sm font-semibold text-gray-900 dark:text-white">Importar desde CSV</h3>

      <div>
        <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
          Si una fila ya existe…
        </label>
        <select
          [(ngModel)]="mode"
          [disabled]="importing"
          class="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 disabled:opacity-50">
          <option value="SKIP">Ignorar (no tocar el registro existente)</option>
          <option value="UPDATE">Actualizar (merge parcial — vacíos no sobrescriben)</option>
          <option value="ERROR">Fallar la fila y reportarla en errores</option>
        </select>
      </div>

      <div>
        <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
          Archivo CSV (UTF-8, máx. 5 MB / 5000 filas)
        </label>
        <input
          type="file"
          accept=".csv,text/csv"
          (change)="onFileChange($event)"
          [disabled]="importing"
          class="block w-full text-sm text-gray-700 dark:text-gray-200 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-brand-50 file:text-brand-700 hover:file:bg-brand-100 dark:file:bg-brand-500/10 dark:file:text-brand-300" />
        @if (fileError) {
          <p class="mt-1 text-xs text-red-600 dark:text-red-400">{{ fileError }}</p>
        }
      </div>

      <button
        type="button"
        (click)="importar()"
        [disabled]="!selectedFile || importing"
        class="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg bg-brand-500 text-white hover:bg-brand-600 disabled:opacity-50 disabled:cursor-not-allowed">
        @if (importing) {
          <svg class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
          </svg>
          Procesando…
        } @else {
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M17 8l-5-5m0 0L7 8m5-5v12"></path>
          </svg>
          Importar
        }
      </button>
    </div>
    }

    <!-- Resultado -->
    @if (result) {
      <div class="space-y-3 pt-2 border-t border-gray-200 dark:border-gray-700">
        <h3 class="text-sm font-semibold text-gray-900 dark:text-white">Resultado</h3>
        <div class="grid grid-cols-2 sm:grid-cols-5 gap-2 text-center">
          <div class="rounded-lg bg-gray-50 dark:bg-gray-800 p-2">
            <p class="text-xs text-gray-500 dark:text-gray-400">Total</p>
            <p class="text-lg font-semibold text-gray-900 dark:text-white">{{ result.totalRows }}</p>
          </div>
          <div class="rounded-lg bg-green-50 dark:bg-green-500/10 p-2">
            <p class="text-xs text-green-700 dark:text-green-300">Creados</p>
            <p class="text-lg font-semibold text-green-700 dark:text-green-300">{{ result.created }}</p>
          </div>
          <div class="rounded-lg bg-blue-50 dark:bg-blue-500/10 p-2">
            <p class="text-xs text-blue-700 dark:text-blue-300">Actualizados</p>
            <p class="text-lg font-semibold text-blue-700 dark:text-blue-300">{{ result.updated }}</p>
          </div>
          <div class="rounded-lg bg-amber-50 dark:bg-amber-500/10 p-2">
            <p class="text-xs text-amber-700 dark:text-amber-300">Omitidos</p>
            <p class="text-lg font-semibold text-amber-700 dark:text-amber-300">{{ result.skipped }}</p>
          </div>
          <div class="rounded-lg bg-red-50 dark:bg-red-500/10 p-2">
            <p class="text-xs text-red-700 dark:text-red-300">Fallaron</p>
            <p class="text-lg font-semibold text-red-700 dark:text-red-300">{{ result.failed }}</p>
          </div>
        </div>

        @if (result.errors && result.errors.length > 0) {
          <div class="space-y-2">
            <div class="flex items-center justify-between">
              <h4 class="text-xs font-semibold text-gray-900 dark:text-white uppercase tracking-wide">
                Errores ({{ result.errors.length }}{{ result.errors.length === 200 ? '+' : '' }})
              </h4>
              @if (result.errors.length > 10) {
                <button
                  type="button"
                  (click)="showAllErrors = !showAllErrors"
                  class="text-xs text-brand-600 dark:text-brand-400 hover:underline">
                  {{ showAllErrors ? 'Mostrar solo 10' : 'Ver todos' }}
                </button>
              }
            </div>
            <div class="max-h-60 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700">
              <table class="w-full text-xs">
                <thead class="bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300 sticky top-0">
                  <tr>
                    <th class="px-3 py-2 text-left font-medium w-16">Fila</th>
                    <th class="px-3 py-2 text-left font-medium w-32">Campo</th>
                    <th class="px-3 py-2 text-left font-medium">Mensaje</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-gray-200 dark:divide-gray-700">
                  @for (err of visibleErrors; track $index) {
                    <tr class="bg-white dark:bg-gray-900">
                      <td class="px-3 py-1.5 text-gray-700 dark:text-gray-200">{{ err.rowNumber }}</td>
                      <td class="px-3 py-1.5 text-gray-700 dark:text-gray-200">{{ err.field || '—' }}</td>
                      <td class="px-3 py-1.5 text-red-600 dark:text-red-400">{{ err.message }}</td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          </div>
        }
      </div>
    }
  </div>
</app-modal>
